/**
 * @file docStorageService.ts
 * @description
 * This service handles all document storage interactions with Supabase:
 * 1) Uploading **user-uploaded** documents to the 'user-uploaded-docs' bucket
 *    and creating corresponding entries in the `documents` table with `user_id` set to the uploader.
 * 2) **NEW**: Uploading **system-generated** documents (like PDF or Markdown from final submission data)
 *    to the 'tempus-created-docs' bucket. For these entries, `user_id = null` to indicate they were
 *    generated by the system rather than a user.
 *
 * The service also offers functions to:
 *  - Fetch documents by submission + fieldName
 *  - Remove documents from a submission while preserving their stored bytes
 *  - Retrieve public URLs
 *
 * Key responsibilities:
 * - Maintain separation between user documents vs. system-generated docs through separate buckets.
 * - Provide robust error handling and logging at each step.
 * - Preserve uploaded bytes when metadata acknowledgements are uncertain.
 *
 * @dependencies
 * - Supabase JS client
 * - UUID library for unique IDs
 *
 * @notes
 * - The code uses two buckets: 'user-uploaded-docs' for user-driven uploads,
 *   and 'tempus-created-docs' for system-generated PDF/MD documents.
 * - Inserted rows in `documents` differ by user_id:
 *     - user-uploaded docs: user_id = actual user
 *     - system-generated docs: user_id = null
 * - The new function `uploadSystemGeneratedDocument` implements the plan from "Step 2" in the docs.
 */

import { getSupabaseClient } from '../lib/supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import { manageDocument } from './documentWorkflowService';

/**
 * We re-use a shared or local Supabase client. For demonstration, we re-initialize here.
 * In a real codebase, you might import from a central supabaseClient.ts.
 */
const supabase = getSupabaseClient();

/** 
 * Bucket for user-driven uploads 
 */
const USER_BUCKET = 'user-uploaded-docs';

/** 
 * Bucket for system-generated PDF/Markdown 
 */
const SYSTEM_GENERATED_BUCKET = 'tempus-created-docs';

/**
 * Helper function for structured logging across all docStorageService operations.
 */
function logOperation(operation: string, details: Record<string, any>, error?: any) {
  const timestamp = new Date().toISOString();
  const logObj = {
    operation,
    timestamp,
    details,
    ...(error && { 
      error: typeof error === 'object'
        ? {
            message: error.message,
            name: error.name,
            code: (error as any).code,
            stack: error.stack,
          }
        : error
    })
  };
  
  if (error) {
    console.error(`STORAGE_LOG [${operation}]:`, logObj);
  } else {
    console.log(`STORAGE_LOG [${operation}]:`, logObj);
  }
  return logObj;
}

/**
 * DocumentMetadata
 * ----------------
 * Represents a row in the `documents` table. 
 * 
 * @property id - Unique ID for this document record (UUID)
 * @property submission_id - The submission to which this doc belongs
 * @property user_id - The user who uploaded it (or null if system-generated)
 * @property filename - The file's original or system-constructed name
 * @property file_path - The path to the file in the relevant storage bucket
 * @property uploaded_at - Timestamp of when the doc was inserted
 * @property type - A short string denoting doc type (e.g. 'doc_protocol', 'submission_form_pdf', etc.)
 * @property is_active - Whether the doc is considered active. Optional (may be undefined if not returned).
 */
export interface DocumentMetadata {
  id: string;
  submission_id: string;
  user_id: string | null;
  filename: string;
  file_path: string;
  uploaded_at: string;
  type: string;
  is_active?: boolean; 
}

/**
 * ErrorResponse
 * -------------
 * If an operation fails, we return an object with a message and optional code
 */
export interface ErrorResponse {
  message: string;
  code?: string;
}

/**
 * For multiple-doc uploads, we can return either an error or a success
 * containing the newly created DocumentMetadata array.
 */
interface UploadSuccessResponse {
  documents: DocumentMetadata[];
  errors?: Array<{ filename: string; message: string }>;
}
export type UploadResponse = UploadSuccessResponse | ErrorResponse;

/**
 * isErrorResponse
 * ---------------
 * Helper to check if a given object is an ErrorResponse vs. a doc array
 */
export function isErrorResponse(
  response: DocumentMetadata | ErrorResponse | UploadResponse
): response is ErrorResponse {
  return 'message' in response && !('documents' in response);
}

/* -------------------------------------------------------------------------
    USER-UPLOADED DOCUMENTS SECTION
   -------------------------------------------------------------------------*/

/**
 * uploadSingleDocument
 * --------------------
 * Uploads a single file to the user-driven bucket 'user-uploaded-docs'.
 * Creates a record in `documents` with user_id set to the uploading user.
 * 
 * @param file         The browser File object
 * @param submissionId The submission to associate with
 * @param userId       The uploading user's ID
 * @param fieldName    The form field name, used to set `type` in the DB
 * @returns DocumentMetadata or ErrorResponse
 */
export async function uploadSingleDocument(
  file: File,
  submissionId: string,
  userId: string,
  fieldName: string,
  options: { documentId?: string; inactive?: boolean } = {},
): Promise<DocumentMetadata | ErrorResponse> {
  const documentId = options.documentId || uuidv4();
  const extension = file.name.split('.').pop()?.toLowerCase() || 'pdf';
  const filePath = `${submissionId}/${documentId}.${extension}`;
  try {
    if (options.documentId) {
      const { data } = await supabase.from('documents').select('*').eq('id', documentId).eq('submission_id', submissionId).maybeSingle();
      if (data) return data as DocumentMetadata;
    }
    const { error: uploadError } = await supabase.storage.from(USER_BUCKET).upload(filePath, file, {
      cacheControl: '3600', upsert: false, contentType: file.type,
    });
    if (uploadError) {
      // A retry may find the bytes from an upload whose acknowledgement was lost.
      if (options.documentId && /already exists|duplicate|resource exists/i.test(uploadError.message)) {
        const { data, error } = await supabase.storage.from(USER_BUCKET).download(filePath);
        if (error || !data || data.size !== file.size) return { message: 'This upload could not be verified. Please try again.' };
      } else return { message: uploadError.message };
    }
    const documentMetadata: DocumentMetadata = {
      id: documentId, submission_id: submissionId, user_id: userId, filename: file.name,
      file_path: filePath, uploaded_at: new Date().toISOString(), type: fieldName,
      is_active: !options.inactive,
    };
    const { data, error } = await supabase.from('documents').insert([documentMetadata]).select('*').single();
    if (!error && data) return data as DocumentMetadata;
    // An insert can commit even if its response is lost. Never delete the file here.
    const { data: recovered } = await supabase.from('documents').select('*').eq('id', documentId).eq('submission_id', submissionId).maybeSingle();
    if (recovered) return recovered as DocumentMetadata;
    logOperation('uploadSingleDocument:metadata-error', { documentId }, error);
    return { message: 'The file upload could not be confirmed. Please retry; your existing documents are unchanged.' };
  } catch (error) {
    logOperation('uploadSingleDocument:unexpected-error', { documentId }, error);
    return { message: 'The file upload could not be confirmed. Please retry.' };
  }
}

/**
 * uploadMultipleDocuments
 * -----------------------
 * For user-driven multiple-file upload. 
 * 
 * @param files        Array of File objects
 * @param submissionId Submission ID
 * @param userId       The uploading user's ID
 * @param fieldName    The form field name used to set `type`
 * @returns UploadResponse (either { documents: DocumentMetadata[] } or { message: string })
 */
export async function uploadMultipleDocuments(
  files: File[],
  submissionId: string,
  userId: string,
  fieldName: string
): Promise<UploadResponse> {
  try {
    const uploadPromises = Array.from(files).map((file) =>
      uploadSingleDocument(file, submissionId, userId, fieldName)
    );
    const results = await Promise.all(uploadPromises);

    return {
      documents: results.filter((result): result is DocumentMetadata => !isErrorResponse(result)),
      errors: results.flatMap((result, index) => isErrorResponse(result)
        ? [{ filename: files[index].name, message: result.message }] : []),
    };
  } catch (error) {
    logOperation('uploadMultipleDocuments:unexpected-error', {}, error);
    return { message: 'An unexpected error occurred during batch upload.' };
  }
}

/* -------------------------------------------------------------------------
    SYSTEM-GENERATED DOCUMENTS SECTION (PDF / MARKDOWN)
   -------------------------------------------------------------------------*/

/**
 * SystemDocType
 * -------------
 * Enumerates the possible system doc types we store in 'tempus-created-docs'.
 * e.g. `'submission_form_pdf'`, `'submission_form_markdown'`
 */
export type SystemDocType = 'submission_form_pdf' | 'submission_form_markdown' | 'stamped_icf';

/**
 * buildSystemGeneratedFilename
 * ----------------------------
 * Constructs a sanitized file name of the form:
 *   refName_sponsorName_protocolNo_YYYY-MM-DD_HHMMss.ext
 * with spaces / special chars replaced by hyphens.
 *
 * @param refName        e.g. "MyStudy"
 * @param sponsorName    e.g. "ACME Inc."
 * @param protocolNumber e.g. "ACME-123"
 * @param extension      e.g. "pdf"
 * @returns A sanitized string suitable for the file name
 */
function buildSystemGeneratedFilename(
  refName: string,
  sponsorName: string,
  extension: string
): string {
  // We'll define a helper to sanitize by replacing non-alphanumeric with hyphens
  // (including spaces, punctuation, etc.)
  const sanitize = (input: string) =>
    input.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/--+/g, '-');

  const dateObj = new Date();
  const datePart = dateObj.toISOString().split('T')[0]; // "YYYY-MM-DD"
  const timePart = dateObj
    .toTimeString()
    .split(' ')[0]
    .replace(/:/g, ''); // "HHMMSS"

  const safeRef = sanitize(refName);
  const safeSponsor = sanitize(sponsorName);

  // e.g. "MyStudy_ACME-Inc_2025-03-14_092012.pdf"
  return `${safeRef}_${safeSponsor}_${datePart}_${timePart}.${extension}`;
}

/**
 * uploadSystemGeneratedDocument
 * -----------------------------
 * Uploads a system-generated Blob (PDF or Markdown) to 'tempus-created-docs',
 * and inserts a row in `documents` with user_id = null, is_active = true.
 *
 * @param args An object containing:
 *   - submissionId: the associated submission's ID
 *   - fileBlob: the binary data representing the PDF or MD file
 *   - docType: e.g. 'submission_form_pdf' or 'submission_form_markdown'
 *   - refName, sponsorName, sponsorProtocolNumber: used for naming
 * @returns A DocumentMetadata or ErrorResponse
 *
 * @example
 * ```
 * await uploadSystemGeneratedDocument({
 *   submissionId: '123e4567-e89b-12d3-a456-426614174000',
 *   fileBlob: myPdfBlob,
 *   docType: 'submission_form_pdf',
 *   refName: 'MyStudy',
 *   sponsorName: 'ACME Inc.',
 *   sponsorProtocolNumber: 'ACME-123'
 * });
 * ```
 *
 * This will produce a filename like `MyStudy_ACME-Inc_ACME-123_2025-03-14_092012.pdf` 
 * and store it at a unique path while retaining the readable display filename.
 */
export async function uploadSystemGeneratedDocument(args: {
  submissionId: string;
  fileBlob: Blob;
  docType: SystemDocType;
  refName: string;
  sponsorName: string;
  sponsorProtocolNumber: string;
}): Promise<DocumentMetadata | ErrorResponse> {
  const {
    submissionId,
    fileBlob,
    docType,
    refName,
    sponsorName,
  } = args;

  // Determine extension from docType
  // For a PDF Blob => "pdf", for markdown => "md"
  let extension = 'pdf';
  if (docType === 'submission_form_markdown') {
    extension = 'md';
  }

  logOperation('uploadSystemGeneratedDocument:start', {
    submissionId,
    docType,
    extension,
  });

  // Build a user-friendly file name
  const finalFilename = buildSystemGeneratedFilename(
    refName,
    sponsorName,
    extension
  );

  // The storage path includes the submissionId folder
  const documentId = uuidv4();
  const filePath = `${submissionId}/${documentId}.${extension}`;

  try {
    // 1) Upload to 'tempus-created-docs'
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(SYSTEM_GENERATED_BUCKET)
      .upload(filePath, fileBlob, {
        cacheControl: '3600',
        upsert: false,
        contentType: docType === 'submission_form_pdf' 
          ? 'application/pdf'
          : 'text/markdown',
      });

    if (uploadError) {
      logOperation('uploadSystemGeneratedDocument:storage-upload-error', { filePath }, uploadError);
      // Possibly file size or other issues
      if (uploadError.message.includes('size')) {
        return { message: 'File size exceeds the maximum allowed limit.' };
      }
      return { message: uploadError.message };
    }

    logOperation('uploadSystemGeneratedDocument:storage-upload-success', {
      filePath,
      uploadData,
    });

    // 2) Insert row in `documents`
    const now = new Date().toISOString();

    const docMeta: DocumentMetadata = {
      id: documentId,
      submission_id: submissionId,
      user_id: null, // system-generated => no user
      filename: finalFilename,
      file_path: filePath,
      uploaded_at: now,
      type: docType,
      is_active: false,
    };

    logOperation('uploadSystemGeneratedDocument:db-insert', {
      documentId,
      filePath,
      docType,
    });

    const { error: dbError } = await supabase
      .from('documents')
      .insert([docMeta]);

    if (dbError) {
      logOperation('uploadSystemGeneratedDocument:db-insert-error', { documentId, filePath }, dbError);
      const { data: recovered } = await supabase.from('documents').select('*').eq('id', documentId).maybeSingle();
      if (recovered) return recovered as DocumentMetadata;
      return { message: 'The generated file could not be confirmed. Please retry.' };
    }

    logOperation('uploadSystemGeneratedDocument:success', {
      documentId,
      docType,
      filePath,
    });

    // Return the newly created doc row
    return docMeta;
  } catch (error) {
    // If something else unexpected happened, log and return an error
    logOperation('uploadSystemGeneratedDocument:unexpected-error', { filePath }, error);
    return { message: 'An unexpected error occurred during system doc upload.' };
  }
}

/* -------------------------------------------------------------------------
    COMMON / SHARED METHODS
   -------------------------------------------------------------------------*/

/**
 * fetchDocuments
 * --------------
 * Retrieve all active documents in the `documents` table for a given submission and type.
 * 
 * @param submissionId The submission ID
 * @param fieldName    The doc type or fieldName
 * @returns An array of DocumentMetadata
 */
export async function fetchDocuments(
  submissionId: string,
  fieldName: string
): Promise<DocumentMetadata[]> {
  try {
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('submission_id', submissionId)
      .eq('type', fieldName)
      .eq('is_active', true);

    if (error) {
      throw new Error(`Documents could not be loaded: ${error.message}`);
    }
    return data as DocumentMetadata[];
  } catch (error) {
    console.error('Failed to fetch documents:', error);
    throw error;
  }
}

/**
 * deleteDocument
 * --------------
 * Marks the selected document inactive through the audited workflow; preserves the stored file.
 * 
 * @param documentId The doc's primary key in DB
 * @param filePath   The storage path
 * @returns true if successful, otherwise an ErrorResponse
 */
/** @deprecated Use manageDocument(id, 'remove'); retained for existing form callers. */
export async function deleteDocument(documentId: string, _filePath: string): Promise<true | ErrorResponse> {
  try {
    await manageDocument(documentId, 'remove');
    return true;
  } catch (error) {
    return { message: error instanceof Error ? error.message : 'The document could not be removed.' };
  }
}

/**
 * getDocumentUrl
 * --------------
 * Returns a public URL for the given file path. For user docs, 
 * we typically rely on RLS, but for system docs, we might want
 * a non-public approach. This function shows a simplified usage.
 * 
 * @param filePath path in the appropriate bucket
 * @param isSystemDoc whether it belongs to the system doc bucket
 */
export function getDocumentUrl(filePath: string, isSystemDoc: boolean = false): string {
  const bucket = isSystemDoc ? SYSTEM_GENERATED_BUCKET : USER_BUCKET;
  const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
  return data.publicUrl;
}
